import { Button, Icon, Pill, Spinner } from '@iwoca/orion';
import classnames from 'classnames';

import styles from './PayLinkStatus.module.css';
import { ExclamationMarkCircledIcon } from '../../../../components/svg/ExclamationMarkCircledIcon';
import { ThreeDotsCircledIcon } from '../../../../components/svg/ThreeDotsCircledIcon';
import { TickIcon } from '../../../../components/svg/TickIcon';
import { RowAppearance } from '../../../../components/Table/Table';
import { getFormattedDate } from '../../../../utils/DisplayDate';
import { useCancelCapturedPaylink } from '../../hooks/useCancelCapturedPaylink';
import {
  PayLinkStatus as PaymentStatus,
  SellerPayLink,
} from '../../hooks/usePaylinks.hook';
import { CopyPayLink } from '../CopyPayLink/CopyPayLink';
import { ThreeDotOptionDropdown } from '../ThreeDotOption/ThreeDotOptionDropdown';

export const PayLinkStatus = ({
  paylink,
  type = 'compact',
  setRowAppearance,
  rowAppearance,
}: {
  paylink: SellerPayLink;
  type?: 'compact' | 'expanded';
  setRowAppearance?: React.Dispatch<React.SetStateAction<RowAppearance>>;
  rowAppearance?: RowAppearance;
}) => {
  const showCopyLinkButton = paylink.status === 'NOT_SIGNED_UP';
  const {
    cancelPayLink,
    isCancellingPayLink,
    errorCancellingPayLink,
    successCancellingPayLink,
  } = useCancelCapturedPaylink({ payLinkId: paylink.id });

  const CancellingPayLink = () => {
    return (
      <div className={styles.cancelPaylinkInProgress}>
        <Spinner size="small" className={styles.cancelSpinner} />{' '}
        <p className={styles.subtitleText}>Cancelling order...</p>
      </div>
    );
  };

  const CancelledPayLink = () => {
    return <>Cancelled</>;
  };

  const PendingCancelPayLink = () => {
    return (
      <div className={styles.actionableCancel}>
        <p className={styles.subtitleText}>
          {errorCancellingPayLink
            ? 'Unable to cancel, try again?'
            : 'Are you sure you want to cancel?'}
        </p>
        <div>
          <Button
            onClick={() => cancelPayLink()}
            variant="secondary-alt"
            data-testid="confirm-cancel"
          >
            <Icon icon={'checkDefault'} fill="green" />
          </Button>
        </div>
        <div>
          <Button
            onClick={() => setRowAppearance!(RowAppearance.HIGHLIGHTED)}
            variant="secondary-alt"
          >
            <Icon icon={'timesDefault'} fill="red" />
          </Button>
        </div>
      </div>
    );
  };

  if (isCancellingPayLink) {
    return <CancellingPayLink />;
  }

  if (successCancellingPayLink) {
    if (setRowAppearance) setRowAppearance(RowAppearance.CANCELLED);
    return <CancelledPayLink />;
  }

  if (setRowAppearance && rowAppearance === RowAppearance.CANCEL) {
    return <PendingCancelPayLink />;
  }

  if (!paylink.status) return <p>Unknown</p>;

  return (
    <div className={styles.status}>
      <StatusIcon status={paylink.status!} className={styles.statusIcon} />
      <div className={classnames(styles.main, styles[type])}>
        <div className={classnames(styles.statusText, styles.statusGrid)}>
          <div>
            <StatusPip status={paylink.status!}>
              <StatusDescriptionText
                status={paylink.status!}
                paylink={paylink}
              />
            </StatusPip>
            {paylink.status === 'SCHEDULED_FOR_CAPTURE' ? (
              <p className={styles.capturedPaymentDate}>
                To be paid on {/*// @ts-ignore*/}
                {new Date(paylink.capture_by).toLocaleDateString('en-GB')}
              </p>
            ) : null}
          </div>
          {paylink.status === 'SCHEDULED_FOR_CAPTURE' && rowAppearance ? (
            <div>
              <ThreeDotOptionDropdown
                className={styles.threeDots}
                options={[
                  {
                    onPress: () => {
                      setRowAppearance!(RowAppearance.CANCEL);
                    },
                    title: 'Cancel order',
                  },
                ]}
              />
            </div>
          ) : null}
        </div>
        {showCopyLinkButton && (
          <div className={classnames(styles.copylinkButton, styles[type])}>
            <CopyPayLink payLink={paylink} />
          </div>
        )}
      </div>
    </div>
  );
};

export const StatusDescriptionText = ({
  paylink,
  status,
}: {
  paylink: SellerPayLink;
  status: PaymentStatus;
}) => {
  const getStatusDescriptionText = () => {
    switch (status) {
      case 'NOT_SIGNED_UP':
        return 'Not signed up';
      case 'SIGNED_UP':
        return 'Signed up';
      case 'APPROVED':
        return 'Approved';
      case 'PARTIALLY_APPROVED':
        return 'Declined';
      case 'DECLINED':
        return 'Declined';
      case 'PAID':
        return `Paid on ${getFormattedDate(paylink.funding_date)}`;
      case 'SCHEDULED_FOR_CAPTURE':
        return 'Pending funds';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  return <>{getStatusDescriptionText()}</>;
};

export const StatusIcon = ({
  status,
  className,
}: {
  status: PaymentStatus;
  className?: string;
}) => {
  const classes = classnames(styles.statusIcon, className);

  switch (status) {
    case 'NOT_SIGNED_UP':
      return (
        <ThreeDotsCircledIcon
          title="Not signed up three dots icon"
          className={classes}
        />
      );
    case 'SIGNED_UP':
      return (
        <ThreeDotsCircledIcon
          title="Signed up three dots icon"
          className={classnames(classes, styles.darkThreeDots)}
        />
      );
    case 'APPROVED':
      return (
        <TickIcon
          title="Approved application tick"
          className={classes}
          fill="#ffbf00"
        />
      );
    case 'PARTIALLY_APPROVED':
      return <ExclamationMarkCircledIcon className={classes} />;
    case 'DECLINED':
      return <ExclamationMarkCircledIcon className={classes} />;
    case 'PAID':
      return <TickIcon title="Funded application tick" className={classes} />;
    case 'SCHEDULED_FOR_CAPTURE':
      return null;
    case 'CANCELLED':
      return <Icon icon="circleEmpty" fill="gray" className={classes} />;
    default:
      return <>Unknown</>;
  }
};

export const StatusPip = ({
  status,
  children,
}: {
  status: PaymentStatus;
  children: JSX.Element;
}): JSX.Element => {
  switch (status) {
    case 'SCHEDULED_FOR_CAPTURE':
      return (
        <Pill variant="green" size="small">
          {children}
        </Pill>
      );
    default:
      return <>{children}</>;
  }
};
